import React, { useState, useEffect } from "react";
import moment from "moment";
import { BarChart, Bar, Cell, XAxis, Tooltip, ReferenceLine } from "recharts";
import { AutoSizer } from "react-virtualized";
import Slider from "rc-slider";
import { Button } from "aible";
import styles from "./style.module.css";
import * as nextReducer from "containers/Next/NextReducer";
import DateSelector from "components/ChatGPT/Dashboard/DateSelector";
import DateInput from "components/ChatGPT/Dashboard/DateInput";
import { format, isAfter, isBefore, isEqual, max, min, parse } from "date-fns";
import { useSelector } from "types/store";
import { getKPIAnalysisTimeBucket } from "containers/Home/reducer";
const createSliderWithTooltip = Slider.createSliderWithTooltip;
const Range = createSliderWithTooltip(Slider.Range);
// TODO: Backend needs to return time series frequency instead
export const getTimeSeriesFrequencyFromDate = (date) => {
    if (!date) {
        return;
    }
    if (parse(date, "yyyy", new Date()).toString() !== "Invalid Date") {
        return "Annual";
    }
    else if (parse(date, "yyyy-MM", new Date()).toString() !== "Invalid Date") {
        return "Monthly";
    }
    else if (parse(date, "yyyy-MM-dd", new Date()).toString() !== "Invalid Date") {
        return "Weekly";
    }
    else {
        console.log("What a hell are you?", date);
        return;
    }
};
export const getDatefnsParseFormat = (date) => {
    const timeSeriesFrequency = getTimeSeriesFrequencyFromDate(date);
    if (timeSeriesFrequency === "Annual") {
        return "yyyy";
    }
    else if (timeSeriesFrequency === "Monthly") {
        return "yyyy-MM";
    }
    else if (timeSeriesFrequency === "Weekly") {
        return "yyyy-MM-dd";
    }
    else {
        return "yyyy-MM-dd";
    }
};
const SmallDateChart = ({ datasetId, field, comparisonOptions, updateComparisonOptions, isDisabled, isSmall, hideChart, hideText, style, currentLabel, referenceLabel, isSense, isManualApply, timeBucketOverride }) => {
    const timeBucket = useSelector((state) => {
        if (timeBucketOverride) {
            return timeBucketOverride;
        }
        if (datasetId) {
            return getKPIAnalysisTimeBucket(state, datasetId);
        }
        return;
    });
    const dates = (field?.data ?? []).map((d) => ({ value: d.value, ["Total Rows"]: d.count }));
    const filteredDates = dates?.filter((d) => d?.value !== "{Missing}");
    const lastIndex = Math.max(filteredDates.length - 1, 0);
    const secondToLastIndex = Math.max(filteredDates.length - 2, 0);
    // set defaults
    let currentStart = field?.data?.[secondToLastIndex]?.value;
    let currentEnd = field?.data?.[lastIndex]?.value;
    let referenceStart = field?.data?.[0]?.value;
    let referenceEnd = field?.data?.[secondToLastIndex]?.value;
    // only use comparisonOptions if it exists
    if (comparisonOptions) {
        const compCurrentStart = nextReducer.getComparisonOptionsCurrentStart(undefined, undefined, comparisonOptions);
        if (compCurrentStart) {
            currentStart = compCurrentStart;
        }
        const compCurrentEnd = nextReducer.getComparisonOptionsCurrentEnd(undefined, undefined, comparisonOptions);
        if (compCurrentEnd) {
            currentEnd = compCurrentEnd;
        }
        const compReferenceStart = nextReducer.getComparisonOptionsReferenceStart(undefined, undefined, comparisonOptions);
        if (compReferenceStart) {
            referenceStart = compReferenceStart;
        }
        const compReferenceEnd = nextReducer.getComparisonOptionsReferenceEnd(undefined, undefined, comparisonOptions);
        if (compReferenceEnd) {
            referenceEnd = compReferenceEnd;
        }
    }
    const [tempReferenceStart, updateTempReferenceStart] = useState(referenceStart);
    const [tempReferenceEnd, updateTempReferenceEnd] = useState(referenceEnd);
    const [tempCurrentStart, updateTempCurrentStart] = useState(currentStart);
    const [tempCurrentEnd, updateTempCurrentEnd] = useState(currentEnd);
    useEffect(() => {
        updateTempReferenceStart(referenceStart);
        updateTempReferenceEnd(referenceEnd);
        updateTempCurrentStart(currentStart);
        updateTempCurrentEnd(currentEnd);
    }, [comparisonOptions?.dateField, referenceStart, referenceEnd, currentStart, currentEnd]);
    const retReferenceStart = isManualApply ? tempReferenceStart : referenceStart;
    const retReferenceEnd = isManualApply ? tempReferenceEnd : referenceEnd;
    const retCurrentStart = isManualApply ? tempCurrentStart : currentStart;
    const retCurrentEnd = isManualApply ? tempCurrentEnd : currentEnd;
    let currentStartIndex = filteredDates.findIndex((d) => d?.value === retCurrentStart);
    if (currentStartIndex === undefined || currentStartIndex < 0) {
        currentStartIndex = 0;
    }
    let currentEndIndex = filteredDates.findIndex((d) => d?.value === retCurrentEnd);
    if (currentEndIndex === undefined || currentEndIndex < 0) {
        currentEndIndex = lastIndex;
    }
    let referenceStartIndex = filteredDates.findIndex((d) => d?.value === retReferenceStart);
    if (referenceStartIndex === undefined || referenceStartIndex < 0) {
        referenceStartIndex = lastIndex;
    }
    let referenceEndIndex = filteredDates.findIndex((d) => d?.value === retReferenceEnd);
    if (referenceEndIndex === undefined || referenceEndIndex < 0) {
        referenceEndIndex = lastIndex;
    }
    const dateRangeIndices = [referenceStartIndex, referenceEndIndex, currentStartIndex, currentEndIndex];
    const updateTempComparisonOptions = (newComparisonOptions) => {
        if (newComparisonOptions.referenceStart !== undefined) {
            updateTempReferenceStart(newComparisonOptions.referenceStart);
        }
        if (newComparisonOptions.referenceEnd !== undefined) {
            updateTempReferenceEnd(newComparisonOptions.referenceEnd);
        }
        if (newComparisonOptions.currentStart !== undefined) {
            updateTempCurrentStart(newComparisonOptions.currentStart);
        }
        if (newComparisonOptions.currentEnd !== undefined) {
            updateTempCurrentEnd(newComparisonOptions.currentEnd);
        }
    };
    const normalizeComparisonOptions = (baseComparisonOptions, updates, changedKey) => {
        const newComparisonOptions = nextReducer.checkValidComparisonOptions({
            ...baseComparisonOptions,
            ...updates
        }, filteredDates.map((d) => d?.value));
        const dateFnsFormat = getDatefnsParseFormat(newComparisonOptions?.currentStart);
        // Keep each selected period ordered from start to end.
        if (newComparisonOptions.referenceStart !== undefined && newComparisonOptions.referenceEnd !== undefined) {
            const newRefStart = parse(newComparisonOptions.referenceStart, dateFnsFormat, new Date());
            const newRefEnd = parse(newComparisonOptions.referenceEnd, dateFnsFormat, new Date());
            newComparisonOptions.referenceStart = format(min([newRefStart, newRefEnd]), dateFnsFormat);
            newComparisonOptions.referenceEnd = format(max([newRefStart, newRefEnd]), dateFnsFormat);
        }
        if (newComparisonOptions.currentStart !== undefined && newComparisonOptions.currentEnd !== undefined) {
            const newCurrStart = parse(newComparisonOptions.currentStart, dateFnsFormat, new Date());
            const newCurrEnd = parse(newComparisonOptions.currentEnd, dateFnsFormat, new Date());
            newComparisonOptions.currentStart = format(min([newCurrStart, newCurrEnd]), dateFnsFormat);
            newComparisonOptions.currentEnd = format(max([newCurrStart, newCurrEnd]), dateFnsFormat);
        }
        // Keep the current period from starting before the reference period.
        if (newComparisonOptions.currentStart !== undefined && newComparisonOptions.referenceStart !== undefined) {
            const newReferenceStart = parse(newComparisonOptions.referenceStart, dateFnsFormat, new Date());
            const newCurrentStart = parse(newComparisonOptions.currentStart, dateFnsFormat, new Date());
            if (isBefore(newCurrentStart, newReferenceStart) || isEqual(newCurrentStart, newReferenceStart)) {
                if (newComparisonOptions.referenceEnd !== undefined) {
                    newComparisonOptions.currentStart = newComparisonOptions.referenceEnd;
                    if (newComparisonOptions.currentEnd !== undefined) {
                        const newCurrentEnd = parse(newComparisonOptions.currentEnd, dateFnsFormat, new Date());
                        const nextCurrentStart = parse(newComparisonOptions.currentStart, dateFnsFormat, new Date());
                        if (isBefore(newCurrentEnd, nextCurrentStart)) {
                            newComparisonOptions.currentEnd = newComparisonOptions.currentStart;
                        }
                    }
                }
                else {
                    const temp = newComparisonOptions.currentStart;
                    newComparisonOptions.currentStart = newComparisonOptions.referenceStart;
                    newComparisonOptions.referenceStart = temp;
                }
            }
        }
        // Keep the reference period from ending after the current period.
        if (newComparisonOptions.currentEnd !== undefined && newComparisonOptions.referenceEnd !== undefined) {
            const newReferenceEnd = parse(newComparisonOptions.referenceEnd, dateFnsFormat, new Date());
            const newCurrentEnd = parse(newComparisonOptions.currentEnd, dateFnsFormat, new Date());
            if (isAfter(newReferenceEnd, newCurrentEnd) || isEqual(newCurrentEnd, newReferenceEnd)) {
                if (newComparisonOptions.currentStart !== undefined) {
                    newComparisonOptions.referenceEnd = newComparisonOptions.currentStart;
                    if (newComparisonOptions.referenceStart !== undefined) {
                        const newReferenceStart = parse(newComparisonOptions.referenceStart, dateFnsFormat, new Date());
                        const nextReferenceEnd = parse(newComparisonOptions.referenceEnd, dateFnsFormat, new Date());
                        if (isAfter(newReferenceStart, nextReferenceEnd)) {
                            newComparisonOptions.referenceStart = newComparisonOptions.referenceEnd;
                        }
                    }
                }
                else {
                    const temp = newComparisonOptions.referenceEnd;
                    newComparisonOptions.referenceEnd = newComparisonOptions.currentEnd;
                    newComparisonOptions.currentEnd = temp;
                }
            }
        }
        // Keep the periods from overlapping.
        if (newComparisonOptions.referenceEnd !== undefined && newComparisonOptions.currentStart !== undefined) {
            const newReferenceEnd = parse(newComparisonOptions.referenceEnd, dateFnsFormat, new Date());
            const newCurrentStart = parse(newComparisonOptions.currentStart, dateFnsFormat, new Date());
            if (changedKey === "currentStart" && isBefore(newCurrentStart, newReferenceEnd)) {
                newComparisonOptions.referenceEnd = newComparisonOptions.currentStart;
            }
            if (changedKey === "referenceEnd" && isAfter(newReferenceEnd, newCurrentStart)) {
                newComparisonOptions.currentStart = newComparisonOptions.referenceEnd;
            }
            if (!changedKey && isAfter(newReferenceEnd, newCurrentStart)) {
                newComparisonOptions.currentStart = newComparisonOptions.referenceEnd;
            }
        }
        return newComparisonOptions;
    };
    const handleTempComparisonOptionValueUpdate = (updates, changedKey) => {
        const newComparisonOptions = normalizeComparisonOptions({
            ...comparisonOptions,
            referenceStart: tempReferenceStart,
            referenceEnd: tempReferenceEnd,
            currentStart: tempCurrentStart,
            currentEnd: tempCurrentEnd
        }, updates, changedKey);
        updateTempComparisonOptions(newComparisonOptions);
    };
    const handleApplyChanges = () => {
        const newComparisonOptions = {
            ...comparisonOptions,
            referenceStart: tempReferenceStart,
            referenceEnd: tempReferenceEnd,
            currentStart: tempCurrentStart,
            currentEnd: tempCurrentEnd
        };
        // Have to do this twice for some reason
        updateComparisonOptions(newComparisonOptions);
        setTimeout(() => updateComparisonOptions(newComparisonOptions), 0);
    };
    const handleTempSliderUpdate = (range) => {
        if (!range) {
            return;
        }
        handleTempComparisonOptionValueUpdate({
            referenceStart: filteredDates[range[0]]?.value,
            referenceEnd: filteredDates[range[1]]?.value,
            currentStart: filteredDates[range[2]]?.value,
            currentEnd: filteredDates[range[3]]?.value
        });
    };
    const handleSliderUpdate = (range) => {
        if (!range) {
            return;
        }
        const newComparisonOptions = {
            ...comparisonOptions,
            referenceStart: referenceStart,
            referenceEnd: referenceEnd,
            currentStart: currentStart,
            currentEnd: currentEnd
        };
        const newRefStart = filteredDates[range[0]]?.value;
        const newRefEnd = filteredDates[range[1]]?.value;
        const newCurrStart = filteredDates[range[2]]?.value;
        const newCurrEnd = filteredDates[range[3]]?.value;
        if (newRefStart !== undefined) {
            newComparisonOptions.referenceStart = newRefStart;
        }
        if (newRefEnd !== undefined) {
            newComparisonOptions.referenceEnd = newRefEnd;
        }
        if (newCurrStart !== undefined) {
            newComparisonOptions.currentStart = newCurrStart;
        }
        if (newCurrEnd !== undefined) {
            newComparisonOptions.currentEnd = newCurrEnd;
        }
        updateComparisonOptions(newComparisonOptions);
    };
    const handleComparisonOptionValueUpdate = (key, data) => {
        const newComparisonOptions = normalizeComparisonOptions(comparisonOptions, { [key]: data }, key);
        updateComparisonOptions(newComparisonOptions);
    };
    const handleIsCurrentCheck = (date) => {
        const currOptions = {
            referenceStart: retReferenceStart,
            referenceEnd: retReferenceEnd,
            currentStart: retCurrentStart,
            currentEnd: retCurrentEnd
        };
        return nextReducer.getIsCurrentDate(undefined, undefined, date, currOptions);
    };
    const handleIsReferenceCheck = (date) => {
        const currOptions = {
            referenceStart: retReferenceStart,
            referenceEnd: retReferenceEnd,
            currentStart: retCurrentStart,
            currentEnd: retCurrentEnd
        };
        return nextReducer.getIsReferenceDate(undefined, undefined, date, currOptions);
    };
    let shouldHaveLabelLookup = false;
    const dateOptions = filteredDates.map((d) => {
        const option = {
            label: d.value,
            value: d.value
        };
        // Only change the label if the timebucket is year or month
        if (timeBucket === "M") {
            const cDate = parse(d.value, "yyyy-MM-dd", new Date());
            // Only change format if the date is the expected date that includes month and date
            if (cDate.toString() !== "Invalid Date") {
                const formatString = timeBucket === "M" ? "yyyy-MM" : "yyyy";
                option.label = format(cDate, formatString);
                shouldHaveLabelLookup = true;
            }
        }
        return option;
    });
    let labelLookup;
    if (shouldHaveLabelLookup) {
        labelLookup = {};
        dateOptions.forEach((o) => {
            if (labelLookup) {
                labelLookup[o.value] = o.label;
            }
        });
    }
    useEffect(() => {
        const newComparisonOptions = {
            ...comparisonOptions,
            referenceStart: referenceStart,
            referenceEnd: referenceEnd,
            currentStart: currentStart,
            currentEnd: currentEnd
        };
        updateComparisonOptions(newComparisonOptions);
    }, [comparisonOptions?.dateField]);
    return (React.createElement("div", { className: styles.dateChartWrapper, style: style },
        filteredDates?.length > 0 && !hideChart && (React.createElement("div", { className: styles.dateChartGraph },
            React.createElement(AutoSizer, null, ({ width }) => (React.createElement(BarChart, { width: width, height: 40, data: filteredDates, margin: { right: 0, left: 0 } },
                React.createElement(XAxis, { dataKey: "value", hide: true }),
                React.createElement(Tooltip, null),
                React.createElement(Bar, { dataKey: "Total Rows" }, filteredDates.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: handleIsCurrentCheck(entry.value)
                        ? "#34D399"
                        : handleIsReferenceCheck(entry.value)
                            ? "#8884d8"
                            : "#C8CAD4" })))),
                React.createElement(ReferenceLine, { x: retReferenceStart, stroke: "#8884d8" }),
                React.createElement(ReferenceLine, { x: retReferenceEnd, stroke: "#8884d8" }),
                React.createElement(ReferenceLine, { x: retCurrentStart, stroke: "#34D399" }),
                React.createElement(ReferenceLine, { x: retCurrentEnd, stroke: "#34D399" })))))),
        dates && dates.length > 1 && (React.createElement(React.Fragment, null,
            !hideText && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.dateLabel, style: { marginTop: !hideChart ? "15px" : undefined } }, "What dates would you like to use?"),
                React.createElement("div", { className: styles.dateLabelHelper }, "Drag the sliders to select the dates you would like to use"))),
            React.createElement("div", { className: styles.dateChartSliderWrapper, style: { marginTop: !hideText ? "10px" : undefined } },
                React.createElement("div", { className: styles.dateChartSlider, style: {
                        width: hideChart
                            ? undefined
                            : ((Math.max(0, filteredDates.length - 1) / (filteredDates.length || 1)) * 100 || 100) + "%"
                    } },
                    React.createElement(DateSelector, { dateIndices: dateRangeIndices, updateDateIndices: isManualApply ? handleTempSliderUpdate : handleSliderUpdate, dateArray: filteredDates.map((d) => d.value), labelLookup: labelLookup, isDisabled: isDisabled }))),
            React.createElement("div", { className: styles.dateChartInputs, style: {
                    display: isSmall ? "block" : undefined,
                    flexWrap: isSense ? "wrap" : undefined
                } },
                React.createElement(DateInput, { label: referenceLabel ?? "Reference Period", labelIconColor: "#7E69D7", startDate: retReferenceStart, endDate: retReferenceEnd, updateStartDate: (v) => isManualApply
                        ? handleTempComparisonOptionValueUpdate({ referenceStart: v }, "referenceStart")
                        : handleComparisonOptionValueUpdate("referenceStart", v), updateEndDate: (v) => isManualApply
                        ? handleTempComparisonOptionValueUpdate({ referenceEnd: v }, "referenceEnd")
                        : handleComparisonOptionValueUpdate("referenceEnd", v), dateOptions: dateOptions, isDisabled: isDisabled, isSense: isSense }),
                React.createElement(DateInput, { label: currentLabel ?? "Current Period", labelIconColor: "#34D399", labelStyle: {
                        justifyContent: !isSmall && !isSense ? "flex-end" : undefined
                    }, startDate: retCurrentStart, endDate: retCurrentEnd, updateStartDate: (v) => isManualApply
                        ? handleTempComparisonOptionValueUpdate({ currentStart: v }, "currentStart")
                        : handleComparisonOptionValueUpdate("currentStart", v), updateEndDate: (v) => isManualApply
                        ? handleTempComparisonOptionValueUpdate({ currentEnd: v }, "currentEnd")
                        : handleComparisonOptionValueUpdate("currentEnd", v), dateOptions: dateOptions, isDisabled: isDisabled, isSense: isSense })))),
        isManualApply && (React.createElement("div", { className: styles.smallDateFooter },
            React.createElement(Button, { onClick: () => handleApplyChanges(), disabled: tempReferenceStart === referenceStart &&
                    tempReferenceEnd === referenceEnd &&
                    tempCurrentStart === currentStart &&
                    tempCurrentEnd === currentEnd }, "Apply")))));
};
// Legacy Version for setup
export const SmallDateChartOld = ({ field, dateValue, updateDateValue, dateFilterValue, updateDateFilterValue, handleUpdate, isDisabled, hideChart, hideText, style }) => {
    const defaultDateRangeIndices = [0, field?.data ? field?.data.length - 1 : 0];
    if (field?.data && dateValue !== undefined) {
        defaultDateRangeIndices[1] = field?.data.findIndex((d) => d?.value === dateValue);
    }
    if (field?.data && dateFilterValue !== undefined) {
        defaultDateRangeIndices[0] = field?.data.findIndex((d) => d?.value === dateFilterValue);
    }
    const [dateRangeIndices, updateDateRangeIndices] = useState(defaultDateRangeIndices);
    useEffect(() => {
        const newDateRangeIndices = [...dateRangeIndices];
        if (field?.data && dateValue !== undefined) {
            newDateRangeIndices[1] = field?.data.findIndex((d) => d?.value === dateValue);
        }
        if (field?.data && dateFilterValue !== undefined) {
            newDateRangeIndices[0] = field?.data.findIndex((d) => d?.value === dateFilterValue);
        }
        updateDateRangeIndices(newDateRangeIndices);
    }, [dateValue, dateFilterValue]);
    const handleSliderUpdate = (range) => {
        if (!range || range.some((e) => !isFinite(e) || e < 0)) {
            return;
        }
        const retDateValue = field?.data?.[range[1]];
        const retDateFilterValue = range[0] === 0 ? undefined : field?.data?.[range[0]];
        if (handleUpdate) {
            handleUpdate(retDateValue, retDateFilterValue);
        }
        else {
            if (updateDateValue) {
                updateDateValue(retDateValue);
            }
            if (updateDateFilterValue) {
                updateDateFilterValue(retDateFilterValue);
            }
        }
        updateDateRangeIndices(range);
    };
    const data = (field?.data ?? []).map((d) => ({ value: d.value, ["Total Rows"]: d.count }));
    const dateFilterValueMoment = moment(dateFilterValue);
    const dateValueMoment = moment(dateValue);
    const sliderRange = [0, data ? data.length - 1 : 0];
    // const retDateRangeIndices = [0, ...dateRangeIndices, sliderRange[1]];
    const retDateRangeIndices = dateRangeIndices;
    const sliderHeight = 20;
    const minValue = field?.data?.[retDateRangeIndices?.[0]]?.value;
    const maxValue = field?.data?.[retDateRangeIndices?.[1]]?.value;
    return (React.createElement("div", { className: styles.dateChartWrapper, style: style },
        data?.length > 0 && !hideChart && (React.createElement("div", { className: styles.dateChartGraph },
            React.createElement(AutoSizer, null, ({ width }) => (React.createElement(BarChart, { width: width, height: 40, data: data },
                React.createElement(XAxis, { dataKey: "value", hide: true }),
                React.createElement(Tooltip, null),
                React.createElement(Bar, { dataKey: "Total Rows" }, data.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: dateFilterValue !== undefined && moment(entry.value).isBefore(dateFilterValueMoment)
                        ? "#C8CAD4"
                        : dateValue !== undefined && moment(entry.value).isSameOrAfter(dateValueMoment)
                            ? "#34D399"
                            : "#8884d8" })))),
                dateValue && React.createElement(ReferenceLine, { x: dateValue, stroke: "#8884d8" }),
                dateFilterValue && React.createElement(ReferenceLine, { x: dateFilterValue, stroke: "#8884d8" })))))),
        React.createElement(React.Fragment, null,
            !hideText && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.dateLabel, style: { marginTop: "15px" } }, "What dates would you like to use?"),
                React.createElement("div", { className: styles.dateLabelHelper }, "Drag the sliders to select the dates you would like to use"))),
            React.createElement("div", { className: styles.dateChartSliderWrapper, style: { marginTop: hideChart ? "0px" : undefined } },
                React.createElement("div", { className: styles.sValueLabel, style: { marginRight: "10px" }, title: minValue }, minValue),
                React.createElement("div", { className: styles.dateChartSlider, style: {
                        width: ((Math.max(0, data.length - 1) / (data.length || 1)) * 100 || 100) + "%",
                        padding: `0px ${6}px`
                    } },
                    React.createElement(Range, { min: sliderRange[0], max: sliderRange[1], disabled: isDisabled, onChange: (e) => {
                            handleSliderUpdate(e.slice(1, -1));
                        }, value: [sliderRange[0], ...retDateRangeIndices, sliderRange[1]], step: 1, railStyle: { backgroundColor: "#C8CAD4", height: 5 }, trackStyle: [
                            { backgroundColor: "#C8CAD4", height: 5, borderRadius: 0 },
                            { backgroundColor: "#8884d8", height: 5, borderRadius: 0 },
                            { backgroundColor: "#34D399", height: 5, borderRadius: 0 }
                        ], handleStyle: [
                            {
                                height: sliderHeight,
                                width: 0,
                                cursor: "default",
                                borderColor: "#FFFFFF",
                                borderWidth: 0,
                                borderRadius: 2,
                                backgroundColor: "#7E69D700",
                                marginTop: 0,
                                marginLeft: 0,
                                zIndex: -100
                            },
                            {
                                position: "absolute",
                                height: sliderHeight,
                                width: 4,
                                borderColor: "#FFFFFF",
                                borderWidth: 0,
                                borderRadius: 0,
                                backgroundColor: "#7E69D7",
                                marginTop: -8,
                                marginLeft: 0,
                                zIndex: 1
                            },
                            {
                                position: "absolute",
                                height: sliderHeight,
                                width: 4,
                                borderColor: "#FFFFFF",
                                borderWidth: 0,
                                borderRadius: 0,
                                backgroundColor: "#34D399",
                                marginTop: -8,
                                marginLeft: 0,
                                zIndex: 1
                            },
                            {
                                height: sliderHeight,
                                width: 0,
                                cursor: "default",
                                borderColor: "#FFFFFF",
                                borderWidth: 0,
                                borderRadius: 2,
                                backgroundColor: "#7E69D700",
                                marginTop: 0,
                                marginLeft: 0,
                                zIndex: -100
                            }
                        ], tipProps: {
                            placement: "top",
                            prefixCls: "rc-slider-tooltip"
                        }, tipFormatter: (value) => {
                            return React.createElement("div", { className: styles.dateSliderTooltip }, data && data[value] ? data[value].value : "");
                        } })),
                React.createElement("div", { className: styles.sValueLabel, style: { marginLeft: "10px" }, title: maxValue }, maxValue)))));
};
export default SmallDateChart;
